import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { ChoiceSelect, NotPermitted, ReasonDialog, StatusChip } from '@/design';
import { ApiError } from '@/domains/api';
import { formatDateTime } from '@/domains/survey';
import type {
  IParticipantTerritories,
  IParticipantTerritory,
  IRankingList,
  ITerritoryOverrideHistory,
  TerritoryMatch
} from '../../api/ranking-contracts';
import { rankingsApi } from '../../api/rankings-api';
import { absent } from '../../components/account-facts';
import { HistoryPopover, type IHistoryPage } from '../../components/history-popover';
import { LoadError, useAdminData } from '../../components/admin-page';
import { NOT_ARRIVED } from '../../components/field-controls';
import { TableFrame } from '../../components/table-frame';
import { refusalMessage } from '../../refusal';
import { who } from './rankings-history';
import { isNotFound, OUT_OF_DATE, rereadsAfter, unanswered } from './said';

/**
 * The member's **territories** on the survey's territorial lists (SR-319, RNK-11), under the
 * placements in the hub's Ranking placements fold: per list with a vocabulary, the territory in
 * force, what placed the office there — staff's override, a zip rule, a city rule or the rest of
 * the state — and the office derivation reads. Staff with `rankings.manage` set an override (a live
 * territory of the list, with a reason) and remove it (with a reason); derivation never replaces
 * one (AC3). Lists with no vocabulary are not drawn: nothing derives there.
 *
 * A list with segments says the participant's segment and the share it was told by, under "Placed
 * by"; each row's history button pages staff's overrides of that list, newest first.
 *
 * Read with the placements — on `written`, so a write here or a placement's re-reads it — and a
 * write re-reads the hub, whose `written` moves both: the territory-mismatch flag of a placement
 * (SR-506) follows the territory in force.
 */
export function RankingTerritories({
  participantId,
  keptUnder,
  name,
  lists,
  permitted,
  notPermittedId,
  written,
  reread
}: {
  participantId: string;
  /** The survey the placements and territories are kept under. */
  keptUnder: string;
  name: string;
  lists: IRankingList[];
  permitted: boolean;
  /** The id of the tables' `NotPermitted` sentence, which says why the presses are off. */
  notPermittedId: string;
  written: number;
  reread: () => Promise<boolean>;
}) {
  const { data, error, reload } = useAdminData<IParticipantTerritories>(
    async (signal) => {
      try {
        return await rankingsApi.territories(participantId, keptUnder, signal);
      } catch (readError) {
        if (!(readError instanceof ApiError) && !signal.aborted) console.error(readError);
        throw new Error(refusalMessage(readError, 'The territories could not be read.'));
      }
    },
    [participantId, keptUnder, written]
  );
  const [asked, setAsked] = React.useState<Nullable<Asked>>(null);
  const [open, setOpen] = React.useState(false);
  // The read a write landed over: its rows are out of date, and their presses held, until a read
  // begun after the write lands (`written` moves with the hub's re-read).
  const [heldOver, setHeldOver] = React.useState<Nullable<IParticipantTerritories>>(null);
  const noChoiceId = React.useId();
  const headingId = React.useId();

  if (data === null) {
    return error ? (
      <div className="grid justify-items-start gap-2">
        <LoadError message={error} />
        <Button type="button" size="sm" variant="outline" onClick={() => void reload()}>
          Read again
        </Button>
      </div>
    ) : null;
  }
  if (data.territories.length === 0) return null;

  const labelOf = (list: string) => lists.find((one) => one.list === list)?.label ?? list;
  const ask = (question: Asked) => {
    setAsked(question);
    setOpen(true);
  };

  return (
    <section aria-labelledby={headingId} className="grid gap-2" data-ranking-territories>
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <h4 id={headingId} className="text-sm font-semibold">
          Territories
        </h4>
        <span className="text-sm text-muted-foreground" data-office>
          {data.office.alternate ? 'Primary office (alternate address)' : 'Office'}:{' '}
          {officeText(data.office)}
        </span>
      </div>
      {error && <LoadError message={`Could not refresh — showing the territories as last read. ${error}`} />}
      <TableFrame>
        <Table aria-label={`Territories of ${name}`}>
          <TableHeader>
            <TableRow>
              <TableHead>List</TableHead>
              <TableHead>Territory</TableHead>
              <TableHead>Placed by</TableHead>
              <TableHead>
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.territories.map((row) => (
              <TableRow key={row.list} data-territory-list={row.list}>
                <TableCell className="align-top">{labelOf(row.list)}</TableCell>
                <TableCell className="align-top" data-territory-in-force>
                  {row.territory ?? <span className="text-muted-foreground">None</span>}
                </TableCell>
                <TableCell className="align-top text-sm">
                  <span className="grid gap-0.5">
                    <PlacedBy row={row} />
                    {row.segment && <span data-territory-segment>{segmentText(row)}</span>}
                  </span>
                </TableCell>
                <TableCell className="align-top">
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <HistoryPopover
                      label={`the ${labelOf(row.list)} territory`}
                      name={name}
                      emptyText="No override was ever set on this list."
                      read={async (page, signal) =>
                        overrideHistoryPage(
                          await rankingsApi.territoryHistory(
                            participantId,
                            keptUnder,
                            row.list,
                            page,
                            signal
                          )
                        )
                      }
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={!permitted || row.choices.length === 0 || heldOver === data}
                      aria-describedby={
                        !permitted
                          ? notPermittedId
                          : row.choices.length === 0
                            ? noChoiceId
                            : undefined
                      }
                      aria-label={`${row.override ? 'Change the override' : 'Override'} of ${labelOf(row.list)}`}
                      onClick={() => ask({ kind: 'set', row })}
                    >
                      {row.override ? 'Change' : 'Override'}
                    </Button>
                    {row.override && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={!permitted || heldOver === data}
                        aria-describedby={permitted ? undefined : notPermittedId}
                        aria-label={`Remove override of ${labelOf(row.list)}`}
                        onClick={() => ask({ kind: 'clear', row })}
                      >
                        Remove override
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableFrame>
      <span id={noChoiceId} className="sr-only">
        The list holds no live territory to choose.
      </span>
      {asked && (
        <OverrideDialog
          key={`${asked.kind}:${asked.row.list}`}
          asked={asked}
          open={open}
          onOpenChange={setOpen}
          onClosed={() => setAsked(null)}
          label={labelOf(asked.row.list)}
          name={name}
          write={async (reason, territory) => {
            const { list } = asked.row;
            try {
              if (asked.kind === 'set') {
                await rankingsApi.setTerritory(participantId, keptUnder, list, territory, reason);
              } else {
                await rankingsApi.clearTerritory(participantId, keptUnder, list, reason);
              }
              setHeldOver(data);
              void reread();
              return null;
            } catch (failure) {
              if (!(failure instanceof ApiError)) console.error(failure);
              if (rereadsAfter(failure)) void reread();
              return refusalOf(failure);
            }
          }}
        />
      )}
    </section>
  );
}

/** What a dialog is open for: an override to set or change, or one to remove. */
type Asked = { kind: 'set' | 'clear'; row: IParticipantTerritory };

/** The words for what placed an office in a territory by a rule. */
const MATCH_WORDS: Record<Exclude<TerritoryMatch, 'override'>, string> = {
  'postal-code': 'the office’s zip code',
  city: 'the office’s city',
  state: 'the office’s state'
};

/** The "Placed by" cell: the override with its reason, who and when, and what the office would
 *  give without it; else the rule that placed the office; else that none did. */
function PlacedBy({ row }: { row: IParticipantTerritory }) {
  const derived = row.derivedBy && row.derivedBy !== 'override' ? MATCH_WORDS[row.derivedBy] : null;
  const { override } = row;
  if (override === null) {
    return row.derived ? (
      <span>Derived from {derived ?? 'the office'}</span>
    ) : (
      <span className="text-muted-foreground">No rule takes this office</span>
    );
  }
  const fromOffice = row.derived
    ? `${row.derived}, by ${derived ?? 'the office'}`
    : 'no rule takes the office';
  // An override naming a retired territory gives way to the rules, while it still stands.
  const retired = row.territory !== override.territory;
  return (
    <span className="grid gap-0.5" data-territory-override>
      <span className="flex flex-wrap items-center gap-2">
        <StatusChip tone={retired ? 'attention' : 'info'}>
          {retired ? `Override to ${override.territory}, retired` : 'Override'}
        </StatusChip>
        <span>
          {who(override.actor)}, {formatDateTime(override.setAt)}
        </span>
      </span>
      <span>“{override.reason}”</span>
      <span className="text-muted-foreground">From the office: {fromOffice}</span>
    </span>
  );
}

/** A percentage as said: at most one decimal. */
function percent(value: number): string {
  return `${value.toLocaleString('en-US', { maximumFractionDigits: 1 })}%`;
}

/** The participant's segment and what told it: "PW — UHNW 62% of AUM (client segments),
 *  threshold 50%". */
function segmentText(row: IParticipantTerritory): string {
  const threshold = row.pwMinUhnwShare == null ? '' : `, threshold ${percent(row.pwMinUhnwShare)}`;
  const measured =
    row.segmentMetric === 'uhnw-revenue' ? 'of revenue (migrated survey)' : 'of AUM (client segments)';
  return row.uhnwShare == null
    ? `${row.segment} — no UHNW share answered${threshold}`
    : `${row.segment} — UHNW ${percent(row.uhnwShare)} ${measured}${threshold}`;
}

/** A page of override events as the history popover's rows. */
function overrideHistoryPage(history: ITerritoryOverrideHistory): IHistoryPage {
  return {
    items: history.items.map((event, index) => ({
      key: `${history.page}:${index}`,
      at: event.at,
      who: who(event.actor),
      what: event.new === null ? 'Override removed' : `Set to ${event.new}`,
      from: event.old ?? absent('—'),
      to: event.new ?? absent('—'),
      reason: event.reason
    })),
    page: history.page,
    pageSize: history.pageSize,
    total: history.total
  };
}

/** The office as derivation reads it: "San Francisco, CA 94105", or what is missing. */
function officeText(office: IParticipantTerritories['office']): string {
  const place = [office.city, [office.state, office.postalCode].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  return place === '' ? 'no address on file' : place;
}

/** Set, change or remove an override — a reason each, and a territory to set. */
function OverrideDialog({
  asked,
  open,
  onOpenChange,
  onClosed,
  label,
  name,
  write
}: {
  asked: Asked;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClosed: () => void;
  label: string;
  name: string;
  write: (reason: string, territory: string) => Promise<Nullable<string>>;
}) {
  const { row } = asked;
  const [territory, setTerritory] = React.useState(row.override?.territory ?? '');
  const choiceId = React.useId();
  if (asked.kind === 'clear') {
    return (
      <ReasonDialog
        open={open}
        onOpenChange={onOpenChange}
        onClosed={onClosed}
        title="Remove the override"
        description={`${label} for ${name} goes back to ${row.derived ?? 'no territory'}, as the office derives it. The system keeps the reason with the change.`}
        required
        enterConfirms
        confirmLabel="Remove it"
        onConfirm={(reason) => write(reason, '')}
      />
    );
  }
  return (
    <ReasonDialog
      open={open}
      onOpenChange={onOpenChange}
      onClosed={onClosed}
      title={row.override ? 'Change the override' : 'Override the territory'}
      description={`${label} for ${name}. Derivation from the office no longer applies while the override stands. The system keeps the reason with the change.`}
      required
      confirmLabel="Save it"
      incomplete={territory === '' || territory === row.override?.territory}
      onConfirm={(reason) => write(reason, territory)}
    >
      <div className="grid gap-1 text-sm">
        <span id={choiceId} className="text-muted-foreground">
          Territory
        </span>
        <ChoiceSelect
          labelledBy={choiceId}
          value={territory}
          onChange={setTerritory}
          choices={row.choices.map((choice) => ({
            value: choice,
            label: choice === row.derived ? `${choice} (from the office)` : choice
          }))}
        />
      </div>
    </ReasonDialog>
  );
}

/** A refusal in words: an answer that never arrived is `NOT_ARRIVED`; a 404 the page out of date;
 *  anything else survey-api's — a 400 at `territory` or `reason`. */
function refusalOf(error: unknown): string {
  if (unanswered(error)) return NOT_ARRIVED;
  if (isNotFound(error)) return `This list or member is no longer there. ${OUT_OF_DATE}`;
  return refusalMessage(error, 'The territory could not be saved.');
}
